import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Modal, Form, Input, Table, Space, Typography, DatePicker, Tag, Popconfirm, Row, Col, Checkbox, Card, Statistic, Alert, List, Select, Tabs } from 'antd';
import { PlusOutlined, CheckCircleOutlined, ArrowUpOutlined, CalendarOutlined, EditOutlined, TeamOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAcademicYear } from '../context/AcademicYearContext';
import { createAcademicYear, promoteToNewYear, updateAcademicYear } from '../services/academicYearService';
import { getPromotionPaths, updatePromotionPath } from '../services/promotionService';
import dayjs from 'dayjs';
import useResponsive from '../hooks/useResponsive';
import { useNotification } from '../context/NotificationContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Define helper functions for cleaner components
const renderStatusTag = (record, currentYear) => {
    if (record.is_current) {
        return <Tag icon={<CheckCircleOutlined />} color="success">CURRENT</Tag>;
    }
    if (currentYear && dayjs(record.start_date).isAfter(dayjs(currentYear.end_date))) {
        return <Tag color="blue">FUTURE</Tag>;
    }
    return <Tag color="default">PAST</Tag>;
};

const AcademicYearManagementPage = () => {
    const { message } = useNotification();
    const { isMobile } = useResponsive();
    const { currentYear, allYears, changeCurrentYear, loading, fetchYears } = useAcademicYear();

    // Academic Year Modals
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingYear, setEditingYear] = useState(null);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // Promotion Modals & State
    const [isPromoteModalVisible, setIsPromoteModalVisible] = useState(false);
    const [isPromotionConfirmed, setIsPromotionConfirmed] = useState(false);
    const [promoteForm] = Form.useForm();
    const [promoting, setPromoting] = useState(false);

    // Promotion Paths State and Logic
    const [promotionLoading, setPromotionLoading] = useState(true);
    const [promotionCurrentClasses, setPromotionCurrentClasses] = useState([]);
    const [promotionFutureClasses, setPromotionFutureClasses] = useState([]);
    const [promotionSavingId, setPromotionSavingId] = useState(null);

    // --- Memoized Data ---
    const promotableYears = useMemo(() =>
        allYears
            .filter(year => !year.is_current && year.year_name)
            .sort((a, b) => (a.year_name || '').localeCompare(b.year_name || ''))
        , [allYears]);

    const promotionFutureClassOptions = useMemo(() => {
        const options = promotionFutureClasses.map(fc => ({
            value: fc.id,
            label: fc.name
        }));
        options.unshift({ value: null, label: 'Graduates / No Promotion' });
        return options;
    }, [promotionFutureClasses]);


    // ========================================================
    // DATA FETCHING & HANDLERS (Promotion Paths)
    // ========================================================

    const fetchPromotionPaths = useCallback(async () => {
        setPromotionLoading(true);
        try {
            const { currentYearClasses, futureYearClasses } = await getPromotionPaths();
            setPromotionCurrentClasses(currentYearClasses.map(c => ({ ...c, key: c.id })));
            setPromotionFutureClasses(futureYearClasses);
        } catch (error) {
            message.error('Failed to load promotion data.');
        } finally {
            setPromotionLoading(false);
        }
    }, [message]);

    useEffect(() => {
        if (currentYear) {
            fetchPromotionPaths();
        }
    }, [currentYear, fetchPromotionPaths]);

    const handlePromotionPathChange = async (classId, promotesToClassId) => {
        setPromotionSavingId(classId);
        try {
            await updatePromotionPath(classId, promotesToClassId);
            setPromotionCurrentClasses(prev => prev.map(c =>
                c.id === classId ? { ...c, promotes_to_class_id: promotesToClassId } : c
            ));
            message.success('Promotion path saved!');
        } catch (error) {
            message.error('Failed to save path.');
        } finally {
            setPromotionSavingId(null);
        }
    };


    // ========================================================
    // ACADEMIC YEAR CRUD HANDLERS
    // ========================================================

    const handleAddYear = () => {
        setEditingYear(null);
        form.resetFields();
        form.setFieldsValue({
            working_days: [1, 2, 3, 4, 5],
        });
        setIsModalVisible(true);
    };

    const handleEditYear = (record) => {
        setEditingYear(record);
        form.setFieldsValue({
            year_name: record.year_name,
            dates: [dayjs(record.start_date), dayjs(record.end_date)],
            working_days: record.working_days,
        });
        setIsModalVisible(true);
    };

    const handlePromoteYear = () => {
        promoteForm.resetFields();
        setIsPromotionConfirmed(false);
        setIsPromoteModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingYear(null);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const payload = {
                year_name: values.year_name,
                start_date: values.dates[0].format('YYYY-MM-DD'),
                end_date: values.dates[1].format('YYYY-MM-DD'),
                working_days: values.working_days || [],
            };

            if (editingYear) {
                await updateAcademicYear(editingYear.id, payload);
                message.success('Academic Year updated successfully!');
            } else {
                await createAcademicYear(payload);
                message.success('Academic Year created successfully!');
            }

            handleCancel();
            fetchYears();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save year.');
        } finally {
            setSaving(false);
        }
    };

    const handlePromoteOk = async () => {
        try {
            const values = await promoteForm.validateFields();
            setPromoting(true);

            const response = await promoteToNewYear(values.newYearName);
            message.success(response.message || 'Promotion successful! New year is now active.');
            setIsPromoteModalVisible(false);
            await fetchYears();
            // Force reload to reflect the new context globally
            window.location.reload();
        } catch (error) {
            message.error(error.response?.data?.error || 'Promotion failed.');
        } finally {
            setPromoting(false);
        }
    };

    const handleSuggestDates = () => {
        if (!allYears || allYears.length === 0) {
            message.info('No previous academic year found to suggest dates from.');
            return;
        }
        // Assuming allYears is sorted descending by date/name (as is standard)
        const latestYear = allYears[0];
        const lastStartDate = dayjs(latestYear.start_date);
        const lastEndDate = dayjs(latestYear.end_date);

        const newStartDate = lastStartDate.add(1, 'year');
        const newEndDate = lastEndDate.add(1, 'year');

        form.setFieldsValue({
            dates: [newStartDate, newEndDate],
        });
    };

    // ========================================================
    // RENDER FUNCTIONS (Tab Content)
    // ========================================================

    const academicYearColumns = [
        { title: 'Year Name', dataIndex: 'year_name', key: 'year_name', render: (text) => <Text strong>{text}</Text> },
        { title: 'Status', key: 'status', align: 'center', render: (_, record) => renderStatusTag(record, currentYear) },
        { title: 'Start Date', dataIndex: 'start_date', render: (date) => dayjs(date).format('DD MMM, YYYY') },
        { title: 'End Date', dataIndex: 'end_date', render: (date) => dayjs(date).format('DD MMM, YYYY') },
        {
            title: 'Actions', key: 'actions', align: 'center', width: 250,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEditYear(record)} />
                    {!record.is_current && (
                        <Popconfirm
                            okType='default'
                            title={`Set ${record.year_name} as the current year?`}
                            description="This will change the active year for the entire application and trigger data refetches."
                            onConfirm={() => changeCurrentYear(record.id)}
                            okText="Yes, Set Current"
                            cancelText="No"
                        >
                            <Button>Set Current</Button>
                        </Popconfirm>
                    )
                    }
                </Space >
            )
        }
    ];

    const renderAcademicYearMobileList = () => (
        <List
            loading={loading}
            dataSource={allYears}
            renderItem={(item) => (
                <List.Item>
                    <Card style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <Text strong style={{ fontSize: 16 }}>{item.year_name}</Text>
                            {renderStatusTag(item, currentYear)}
                        </div>
                        <Text type="secondary">{dayjs(item.start_date).format('DD MMM, YYYY')} - {dayjs(item.end_date).format('DD MMM, YYYY')}</Text>
                        <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Button icon={<EditOutlined />} onClick={() => handleEditYear(item)} block>Edit</Button>
                                {!item.is_current && (
                                    <Popconfirm okType='default' title={`Set ${item.year_name} as current?`} onConfirm={() => changeCurrentYear(item.id)}>
                                        <Button block>Set Current</Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        </div>
                    </Card>
                </List.Item>
            )}
        />
    );

    const renderAcademicYearsTab = () => (
        <>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card>
                        <Statistic
                            title="Current Active Academic Year"
                            value={currentYear ? currentYear.year_name : 'Loading...'}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card>
                        <Title level={4}>Year Actions</Title>
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                            <Button type="default" icon={<PlusOutlined />} onClick={handleAddYear} block>
                                Add / Edit Year
                            </Button>
                            <Button danger icon={<ArrowUpOutlined />} onClick={handlePromoteYear} block>
                                Promote to New Year
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Card title="Academic Year History" style={{ marginTop: 24 }}>
                {isMobile ? renderAcademicYearMobileList() : (
                    <Table
                        dataSource={allYears.map(y => ({ ...y, key: y.id }))}
                        columns={academicYearColumns}
                        loading={loading}
                        bordered
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                    />
                )}
            </Card>
        </>
    );

    const promotionColumns = [
        {
            title: 'Current Year Class',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Promotes To (Next Year)',
            dataIndex: 'promotes_to_class_id',
            key: 'promotes_to_class_id',
            width: '60%',
            render: (promotesToClassId, record) => (
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select next year's class"
                    value={promotesToClassId}
                    loading={promotionSavingId === record.id}
                    onChange={(value) => handlePromotionPathChange(record.id, value)}
                    optionFilterProp="label"
                    options={promotionFutureClassOptions}
                    allowClear
                />
            )
        },
    ];

    const renderPromotionMobileList = () => (
        <List
            loading={promotionLoading}
            dataSource={promotionCurrentClasses}
            renderItem={(item) => (
                <List.Item>
                    <Card style={{ width: '100%' }}>
                        <Text strong style={{ marginBottom: 8, display: 'block' }}>Class: {item.name}</Text>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Promotes to class..."
                            value={item.promotes_to_class_id}
                            loading={promotionSavingId === item.id}
                            onChange={(value) => handlePromotionPathChange(item.id, value)}
                            optionFilterProp="label"
                            options={promotionFutureClassOptions}
                            allowClear
                        />
                    </Card>
                </List.Item>
            )}
        />
    );

    const renderPromotionPathsTab = () => (
        <div style={{ margin: '0 auto' }}>
            <Alert
                message="Set Promotion Paths"
                description={`Configure how students from classes in the current year (${currentYear?.year_name || '...'}) will be promoted to the next year. You must define the next academic year first.`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Card>
                {promotionFutureClasses.length === 0 && !promotionLoading && (
                    <Alert
                        message="Next Academic Year Required"
                        description="Please create the subsequent academic year in the 'Academic Years' tab before setting promotion paths."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                {isMobile ? renderPromotionMobileList() : (
                    <Table
                        loading={promotionLoading}
                        dataSource={promotionCurrentClasses}
                        columns={promotionColumns}
                        pagination={false}
                        bordered
                    />
                )}
            </Card>
        </div>
    );

    // ========================================================
    // MAIN COMPONENT RENDER
    // ========================================================

    return (
        <div style={{ margin: '0 auto' }}>
            <Title level={2} style={{ marginBottom: 16 }}>Academic Year Management</Title>

            <Tabs defaultActiveKey="years" type="line" size="large">
                <Tabs.TabPane tab={<span><HistoryOutlined /> Academic Years</span>} key="years">
                    {renderAcademicYearsTab()}
                </Tabs.TabPane>
                <Tabs.TabPane tab={<span><TeamOutlined /> Promotion Paths</span>} key="promotion-paths">
                    {renderPromotionPathsTab()}
                </Tabs.TabPane>
            </Tabs>

            {/* --- MODALS (Kept outside tabs) --- */}
            <Modal okType='default' title={editingYear ? "Edit Academic Year" : "Add New Academic Year"} open={isModalVisible} onOk={handleModalOk} onCancel={handleCancel} confirmLoading={saving}>
                <Form form={form} layout="vertical">
                    <Form.Item name="year_name" label="Year Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g., 2025-2026" />
                    </Form.Item>

                    <Form.Item label="Start & End Date">
                        <Form.Item name="dates" noStyle rules={[{ required: true, message: "Please select the date range." }]}>
                            <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                        {!editingYear && (
                            <Button type="link" onClick={handleSuggestDates} style={{ padding: '4px 0' }} disabled={allYears.length === 0}>
                                Suggest Dates Based on Last Year
                            </Button>
                        )}
                    </Form.Item>

                    <Form.Item name="working_days" label="Working Days" initialValue={[1, 2, 3, 4, 5]}>
                        <Checkbox.Group>
                            <Space wrap>
                                <Checkbox value={1}>Mon</Checkbox> <Checkbox value={2}>Tue</Checkbox> <Checkbox value={3}>Wed</Checkbox>
                                <Checkbox value={4}>Thu</Checkbox> <Checkbox value={5}>Fri</Checkbox> <Checkbox value={6}>Sat</Checkbox>
                                <Checkbox value={0}>Sun</Checkbox>
                            </Space>
                        </Checkbox.Group>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Promote to Next Academic Year"
                open={isPromoteModalVisible}
                onOk={handlePromoteOk}
                onCancel={() => setIsPromoteModalVisible(false)}
                okButtonProps={{ disabled: !isPromotionConfirmed }}
                confirmLoading={promoting}
                okText="Yes, Promote Now"
            >
                <Form form={promoteForm} layout="vertical">
                    <Alert
                        message="Warning: Critical Action"
                        description="This will promote all current students according to the defined paths and set the selected year as active. This process is irreversible."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="newYearName" label="Select the Academic Year to Promote Into" rules={[{ required: true, message: "Please select the new academic year." }]}>
                        <Select
                            showSearch
                            placeholder="Select from pre-added academic years"
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            options={promotableYears.map(year => ({
                                value: year.year_name,
                                label: year.year_name,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="confirmation"
                        valuePropName="checked"
                        rules={[{
                            validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('You must confirm to proceed')),
                        }]}
                    >
                        <Checkbox onChange={(e) => setIsPromotionConfirmed(e.target.checked)}>
                            I confirm all promotion paths are correctly set and I wish to proceed.
                        </Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AcademicYearManagementPage;